import * as queryString from 'query-string';
import axios, { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { IWorkflow } from './models/workflow';
import { LoadingIndicator } from '../../loading-indicator';
import SendCode from './send-code';
import SetPassword from './set-password';
import Success from './success';
import Step from './models/step';
import { VerifyCard, VerifyPage } from './styles';
import { Redirect } from 'react-router';

const initialState: IWorkflow = {
    emailAddress: '',
    confirmationCode: '',
    password: '',
    confirmPassword: ''
};

const postConfig: AxiosRequestConfig = {
    headers: {
        'Content-Type': 'application/json'
    }
};

const CreateAccount = () => {
    const [showLoading, setShowLoading] = useState(false);
    const [step, setStep] = useState(Step.SendCode);
    const [response, setResponse] = useState('');
    const [username, setUsername] = useState('');

    const methods = useForm<IWorkflow>({ defaultValues: initialState, mode: 'onSubmit' });
    const { handleSubmit, getValues, setValue } = methods;

    const tokenParam = queryString.parse(window.location.search).t;
    const token = typeof tokenParam == 'string' ? tokenParam : '';

    const post = (url: string, data: object, onSuccess: (results: any) => void) => {
        setResponse('');
        setShowLoading(true);

        return axios.post(url, data, postConfig)
            .then((result: { data: { success: boolean, results?: any } }) => {
                if (result.data.success) {
                    onSuccess(result.data.results);
                }
            })
            .catch(error => {
                setResponse(error.response && typeof error.response.data == 'string' ? error.response.data : 'Something went wrong, please try again');
            })
            .finally(() => {
                setShowLoading(false);
            });
    };

    const sendCode = () => post(
        `${__API__}/user/account-invite/send-code`,
        { token, emailAddress: getValues('emailAddress').trim() },
        () => setStep(Step.SetPassword));

    const resendCode = () => post(
        `${__API__}/user/account-invite/send-code`,
        { token, emailAddress: getValues('emailAddress').trim() },
        () => setValue('confirmationCode', ''));

    const complete = () => {
        const { emailAddress, confirmationCode, password, confirmPassword } = getValues();

        return post(
            `${__API__}/user/account-invite/complete`,
            { token, emailAddress: emailAddress.trim(), confirmationCode: confirmationCode.trim(), password, confirmPassword },
            (results: string) => {
                setUsername(results);
                setStep(Step.Success);
            });
    };

    const handleSave = () => {
        if (step == Step.SendCode) {
            sendCode();
        }

        if (step == Step.SetPassword) {
            complete();
        }

        if (step == Step.Success) {
            setStep(Step.Redirect);
        }
    };

    if (step == Step.Redirect) {
        return <Redirect to={'/login'} />;
    }

    return (
        <VerifyPage>
            <LoadingIndicator
                type='spin'
                color='black'
                loading={showLoading}
                message={'One moment please...'}
            />

            <VerifyCard>
                <FormProvider {...methods}>
                    <Form onSubmit={handleSubmit(handleSave)}>
                        {!token && (
                            <>
                                <div className='verify-card-header'>
                                    <h4>Verify Your Account</h4>
                                </div>
                                <div className='verify-card-body'>
                                    <div className='notice'>
                                        To verify your account, please use the link in your email. If the link has expired, ask your OPO administrator to send a new one.
                                    </div>
                                </div>
                            </>
                        )}

                        {token && step == Step.SendCode && (
                            <SendCode errorMessage={response} />
                        )}

                        {step == Step.SetPassword && (
                            <SetPassword errorMessage={response} onResendCode={resendCode} />
                        )}

                        {step == Step.Success && (
                            <Success username={username} />
                        )}
                    </Form>
                </FormProvider>
            </VerifyCard>
        </VerifyPage>
    );
}

export default CreateAccount;
